import dropdownEnums, {Enums} from 'services/dropdown-enums';
import { camelCaseStringToNormalText } from 'services/utils/generic.methods';

/** Workflow conversation nodes: types available without quick replies. */
const WORKFLOW_BASE_MESSAGE_TYPE_VALUES = [
  Enums.WFQuestionType.None,
  Enums.WFQuestionType.TrueOrFalse,
  Enums.WFQuestionType.ContactInfo,
  Enums.WFQuestionType.Time,
  Enums.WFQuestionType.TimeCalendar,
  Enums.WFQuestionType.CalendarDate,
  Enums.WFQuestionType.FullText
];

/** Only when the node has quick replies. */
const WORKFLOW_QUICK_REPLY_MESSAGE_TYPE_VALUES = [
  Enums.WFQuestionType.MultipleChoice,
  Enums.WFQuestionType.Dropdown
];

/** Friendly labels for workflow UI (enum names stay aligned with backend). */
const WORKFLOW_MESSAGE_TYPE_LABELS = {
  [Enums.WFQuestionType.None]: 'Plain text',
  [Enums.WFQuestionType.TrueOrFalse]: 'True / False',
  [Enums.WFQuestionType.ContactInfo]: 'Contact info',
  [Enums.WFQuestionType.Time]: 'Time',
  [Enums.WFQuestionType.TimeCalendar]: 'Time & calendar',
  [Enums.WFQuestionType.CalendarDate]: 'Calendar date',
  [Enums.WFQuestionType.FullText]: 'Full text',
  [Enums.WFQuestionType.MultipleChoice]: 'Multiple choice',
  [Enums.WFQuestionType.Dropdown]: 'Dropdown'
};

const getMessageTypeLabel = entry => {
  if (WORKFLOW_MESSAGE_TYPE_LABELS[entry.value] != null) {
    return WORKFLOW_MESSAGE_TYPE_LABELS[entry.value];
  }
  return camelCaseStringToNormalText(entry.name, 'heading');
};

export function getConversationMessageTypeOptions(hasQuickReplies) {
  const allowedValues = hasQuickReplies
    ? [
        ...WORKFLOW_BASE_MESSAGE_TYPE_VALUES,
        ...WORKFLOW_QUICK_REPLY_MESSAGE_TYPE_VALUES
      ]
    : WORKFLOW_BASE_MESSAGE_TYPE_VALUES;

  return dropdownEnums.enum_wf_questionTypes
    .filter(entry => allowedValues.includes(entry.value))
    .map(entry => ({
      value: entry.value,
      label: getMessageTypeLabel(entry)
    }));
}

export function normalizeMessageTypeForQuickReplies(messageType, hasQuickReplies) {
  if (
    !hasQuickReplies &&
    WORKFLOW_QUICK_REPLY_MESSAGE_TYPE_VALUES.includes(Number(messageType))
  ) {
    return Enums.WFQuestionType.None;
  }
  return Number(messageType);
}

/** Default when a contact-info node has no field selected yet. */
export const DEFAULT_CONTACT_VISITOR_RESPONSE_TYPE = Enums.VisitorResponseType.Name;

/** Contact-field choices for workflow nodes with messageType ContactInfo (3). */
export const WORKFLOW_CONTACT_FIELD_OPTIONS = [
  { value: Enums.VisitorResponseType.Name, label: 'Name' },
  { value: Enums.VisitorResponseType.Phone, label: 'Phone' },
  { value: Enums.VisitorResponseType.Email, label: 'Email' }
];

/** Maps visitorResponseType to infoToExtract for Blazeoo.AI ResolveContactInfo fallback. */
export function infoToExtractForContactField(visitorResponseType) {
  switch (Number(visitorResponseType)) {
    case Enums.VisitorResponseType.Phone:
      return 'phone';
    case Enums.VisitorResponseType.Email:
      return 'email';
    default:
      return 'name';
  }
}

export function contactFieldsForMessageType(messageType, visitorResponseType) {
  if (Number(messageType) !== Enums.WFQuestionType.ContactInfo) {
    return {};
  }

  const resolvedType =
    visitorResponseType != null
      ? Number(visitorResponseType)
      : DEFAULT_CONTACT_VISITOR_RESPONSE_TYPE;

  return {
    visitorResponseType: resolvedType,
    infoToExtract: infoToExtractForContactField(resolvedType)
  };
}
